import { TopicFilter } from '../interfaces';

export class MqttUtilities {

    /**
     * Builds the topic filter with the given prefix.
     * @public
     * @param {TopicFilter} topic - The topic string, array of strings, or subscription map.
     * @param {string} [topicPrefix] - The topic prefix.
     * @returns {TopicFilter} The built topic filter with prefix applied.
     * @description
     * - If topicPrefix is not provided or not a string, returns the original topic.
     * - If topic is a string: prepends prefix if not already present.
     * - If topic is an array: prepends prefix to each topic string if not already present.
     * - If topic is an ISubscriptionMap: returns a new map with prefixed keys (preserving options).
     */
    public static buildTopicFilter(topic: TopicFilter, topicPrefix?: string): TopicFilter {
        
        // if the topic prefix is not a string, return the topic
        if (typeof topicPrefix !== 'string' || typeof topic === 'string' && topic.includes(topicPrefix)) {
            return topic;
        }
        
        if (Array.isArray(topic)) {
            return topic.map(t => (typeof t === 'string' && topicPrefix) ? `${topicPrefix}${t}` : t);
        }

        return `${topicPrefix}${topic}`;

    }

    /**
     * Tests an incoming topic against a subscription filter.
     * @public
     * @param {string} topic - The concrete topic a message arrived on.
     * @param {string} filter - The filter a handler subscribed with.
     * @returns {boolean} True when the filter covers the topic.
     * @description
     * Implements the MQTT topic-matching rules:
     * - `+` matches exactly one level;
     * - `#` matches every remaining level, including none, and is only legal as
     *   the last level;
     * - neither wildcard matches a `$`-prefixed topic (`$SYS/...`), which the
     *   spec reserves for the broker.
     *
     * Needed because messages are delivered on a concrete topic while handlers
     * are registered under the filter they subscribed with — the two are only
     * the same string for plain, single-topic subscriptions.
     */
    public static matchesTopicFilter(topic: string, filter: string): boolean {

        // The overwhelmingly common case: a plain topic subscribed verbatim.
        if (topic === filter) {
            return true;
        }

        const topicLevels = topic.split('/');
        const filterLevels = filter.split('/');

        // Wildcards never reach into the broker's own topic space.
        if (topic.startsWith('$') && (filterLevels[0] === '+' || filterLevels[0] === '#')) {
            return false;
        }

        for (let i = 0; i < filterLevels.length; i++) {

            const level = filterLevels[i];

            // `#` swallows the rest of the topic, so anything after it is
            // malformed and only a trailing one matches.
            if (level === '#') {
                return i === filterLevels.length - 1;
            }

            // Out of topic levels, or a literal level that disagrees.
            if (i >= topicLevels.length || (level !== '+' && level !== topicLevels[i])) {
                return false;
            }

        }

        // Every filter level matched; the topic must not have extra levels left.
        return topicLevels.length === filterLevels.length;

    }

}
